import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, Check, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';

export default function JoinGroupPage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('');
  const [groupInfo, setGroupInfo] = useState(null);

  useEffect(() => {
    if (!user) {
      // Logged-out users are bounced to the branded landing page with the
      // invite code preserved so they see "You're invited to {group}" before signup.
      navigate(`/welcome?invite=${encodeURIComponent(code)}`, { replace: true });
      return;
    }
    joinGroup();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const joinGroup = async () => {
    try {
      const response = await api.post(`/invites/${code}/join`);
      setStatus('success');
      setMessage(response.data.message);
      setGroupInfo(response.data);
      toast.success(response.data.message);
      setTimeout(() => navigate(`/groups/${response.data.group_id}`), 2000);
    } catch (error) {
      setStatus('error');
      setMessage(error.response?.data?.detail || 'Failed to join group');
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6" data-testid="join-group-page">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-sm"
      >
        <Card className="p-8 border-2 rounded-2xl text-center" data-testid="join-group-card">
          {status === 'loading' && (
            <div data-testid="join-loading">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-primary animate-pulse" />
              </div>
              <h2 className="font-display text-xl font-bold mb-2">Joining Group...</h2>
              <p className="text-muted-foreground text-sm">Please wait</p>
            </div>
          )}

          {status === 'success' && (
            <div data-testid="join-success">
              <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-500" />
              </div>
              <h2 className="font-display text-xl font-bold mb-2">{message}</h2>
              {groupInfo?.group_name && (
                <p className="text-muted-foreground text-sm mb-4">
                  Group: {groupInfo.group_name}
                </p>
              )}
              <p className="text-xs text-muted-foreground">Redirecting...</p>
            </div>
          )}

          {status === 'error' && (
            <div data-testid="join-error">
              <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-destructive" />
              </div>
              <h2 className="font-display text-xl font-bold mb-2">Unable to Join</h2>
              <p className="text-muted-foreground text-sm mb-4">{message}</p>
              <Button onClick={() => navigate('/')} className="rounded-full" data-testid="go-home-btn">
                Go to Dashboard
              </Button>
            </div>
          )}
        </Card>
      </motion.div>
    </div>
  );
}
